import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import TextShift from '../utils/TextShift';
import RevealOnScroll from '../utils/RevealOnScroll';
import GitHubButton from '../utils/GitHubButton';

const PageWrapper = styled.div`
  width: 60%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
    width: 90%;
  }
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: white;
  font-family: "Maitree", sans-serif;
  margin-bottom: 1rem;
`;

const SectionText = styled.p`
  color: #b5b5b5;
  font-family: "Maitree", sans-serif;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const CodeBlock = styled.pre`
  background-color: #1e1e1e;
  padding: 1.5rem;
  border-radius: 8px;
  overflow-x: auto;
  margin: 1rem 0;
  
  code {
    color: #d4d4d4;
    font-family: 'Fira Code', monospace;
    font-size: 0.9rem;
    white-space: pre-wrap;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin: 4rem 0 2rem;
`;

const XMLTools = () => {
  return (
    <>
      <PageWrapper>
        <RevealOnScroll>
          <TitleWrapper>
            <TextShift 
              text="XML Tools" 
              fontFamily="moret-variable"
              fontSize="2rem"
              mobileFontSize="2rem"
            />
            <GitHubButton url="https://github.com/PinknMatter/XML-Tools" />
          </TitleWrapper>
        </RevealOnScroll>

        <Section>
          <SectionTitle>Overview</SectionTitle>
          <SectionText>
            A comprehensive suite of Python-based tools for handling XML file conversions and EDL file editing
            in video production workflows. These tools streamline the process of converting between different
            color correction formats (CC, CDL, CCC) and managing EDL files.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>XML Conversion</SectionTitle>
          <SectionText>
            The XML conversion tool supports converting between different color correction formats:
            CC (Color Correction), CDL (Color Decision List), and CCC (Color Correction Collection).
            The tool preserves all essential color grading information including slope, offset, power,
            and saturation values.
          </SectionText>
            
          <SectionTitle>Command Usage</SectionTitle>
          <CodeBlock>
            <code>
              python [script location]/(File to convert type) (path to file) (output path) (type to convert to){'\n\n'}
              Example:{'\n'}
              python cc.py desktop/grade.cc desktop/ cdl
            </code>
          </CodeBlock>

          <SectionTitle>Core Functionality</SectionTitle>
          <SectionText>
            The conversion tool uses regular expressions to parse XML files and extract color correction
            parameters. Here's the main parsing function:
          </SectionText>
          <CodeBlock>
            <code>
{`def parse_cc(input_file):
    with open(input_file, 'r') as file:
        cc_content = file.read()

    id_match = re.search(r'id="(.*?)"', cc_content)
    slope_match = re.search(r'<Slope>(.*?)</Slope>', cc_content)
    offset_match = re.search(r'<Offset>(.*?)</Offset>', cc_content)
    power_match = re.search(r'<Power>(.*?)</Power>', cc_content)
    saturation_match = re.search(r'<Saturation>(.*?)</Saturation>', cc_content)

    if not all([id_match, slope_match, offset_match, power_match, saturation_match]):
        raise ValueError("Unable to extract required elements from the input file.")

    return id, slope, offset, power, saturation`}
            </code>
          </CodeBlock>

          <SectionTitle>Output Generation</SectionTitle>
          <SectionText>
            The tool can generate both CDL and CCC format files. Here's an example of the CCC generation:
          </SectionText>
          <CodeBlock>
            <code>
{`def write_ccc(output_path, id, slope, offset, power, saturation):
    filename = os.path.join(output_path, f"{id}.ccc")
    with open(filename, 'w') as file:
        file.write('<ColorCorrectionCollection xmlns="urn:ASC:CDL:v1.01">\\n')
        file.write('  <ColorCorrection id="{}">\\n'.format(id))
        file.write('    <SOPNode>\\n')
        file.write('      <Slope>{}</Slope>\\n'.format(slope))
        file.write('      <Offset>{}</Offset>\\n'.format(offset))
        file.write('      <Power>{}</Power>\\n'.format(power))
        file.write('    </SOPNode>\\n')
        file.write('    <SatNode>\\n')
        file.write('      <Saturation>{}</Saturation>\\n'.format(saturation))
        file.write('    </SatNode>\\n')
        file.write('  </ColorCorrection>\\n')
        file.write('</ColorCorrectionCollection>\\n')`}
            </code>
          </CodeBlock>
        </Section>

        <Section>
          <SectionTitle>Edit EDL</SectionTitle>
          <SectionText>
            The EDL editing tool automates the process of adding cut names to EDL (Edit Decision List)
            files. It intelligently processes each entry, skipping MOV files and entries that already
            have cut names, ensuring clean and consistent EDL formatting.
          </SectionText>
            
          <SectionTitle>Command Usage</SectionTitle>
          <CodeBlock>
            <code>
              python [script location]/editedl.py (path to edl file){'\n\n'}
              Example:{'\n'}
              python editedl.py desktop/test.edl
            </code>
          </CodeBlock>

          <SectionTitle>Implementation Details</SectionTitle>
          <SectionText>
            The tool handles different line endings (Windows/Unix) and processes the EDL file line by line.
            Here's the core implementation:
          </SectionText>
          <CodeBlock>
            <code>
{`def normalize_line_endings(text):
    return text.replace("\\r\\n", "\\n").replace("\\r", "\\n")

def edit_edl(edl_file_path):
    # Convert the file path to the appropriate format
    edl_file_path = os.path.normpath(edl_file_path)

    # Read the contents of the EDL file
    with open(edl_file_path, "r") as file:
        edl_contents = file.read()

    # Normalize line endings in the EDL contents
    edl_contents = normalize_line_endings(edl_contents)

    # Modify the specific entries in the EDL
    modified_edl = []
    lines = edl_contents.split("\\n")
    for i, line in enumerate(lines):
        if "* FROM CLIP NAME:" in line:
            clipname = line.split(":")[1].strip()
            # Check if it's a MOV file or if the next line is already a clipname
            next_line = lines[i+1] if i+1 < len(lines) else None
            if ".mov" in line or (next_line and next_line.strip().startswith("*")):
                modified_edl.append(line)
            else:
                modified_edl.append(line)
                modified_edl.append("* " + clipname)
        else:
            modified_edl.append(line)

    # Write the modified contents back to the EDL file
    with open(edl_file_path, "w") as file:
        file.write("\\n".join(modified_edl))`}
            </code>
          </CodeBlock>

          <SectionTitle>Key Features</SectionTitle>
          <SectionText>
            • Normalizes line endings across different operating systems{'\n'}
            • Intelligently skips MOV files to prevent unnecessary modifications{'\n'}
            • Preserves existing cut names{'\n'}
            • Maintains original file formatting{'\n'}
            • Handles file paths appropriately across platforms
          </SectionText>
        </Section>
      </PageWrapper>
    </>
  );
};

export default XMLTools;